import React, { useMemo } from 'react';
import { IOrdersInventoryBal, LogisticsMonthData, TimeGranularity } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import { StrategyChart, DatasetConfig } from './StrategyChart';
import { requiredStockLinePlugin } from './RequiredStockLinePlugin';

interface Props {
  logisticsData: IOrdersInventoryBal[];
  matPrice: number;
  unit: IMassUnitObject;
  onTimeChange: (_time: TimeGranularity) => void;
  materialName: string;
  isCoverage: boolean;
  materialId: string;
}

export const LogisticsInvManagement: React.FC<Props> = ({
  logisticsData,
  matPrice,
  unit,
  onTimeChange,
  materialName,
  isCoverage,
  materialId,
}) => {
  const datasets: DatasetConfig[] = [
    {
      label: isCoverage ? 'Current coverage' : 'Inventory (storage + POs)',
      dataKey: isCoverage ? 'currCover' : 'inventory',
      backgroundColor: '#93C5FD',
      borderColor: '#3B82F6',
      borderWidth: 0,
      borderRadius: 4,
      type: 'bar',
    },
    {
      label: isCoverage ? 'Expected demand' : 'Expected Consumption (MRP + buffer)',
      dataKey: isCoverage ? 'visCovExpectDemand' : 'visExpectDemand',
      borderColor: '#9f9f9f',
      backgroundColor: '#cfcfcf',
      type: 'bar',
      fill: true,
      hidden: false,
    },

    {
      label: isCoverage ? 'Required coverage' : 'Required amount (MRP * strategy)',
      dataKey: isCoverage ? 'reqCover' : 'requiredQuantity',
      backgroundColor: 'red',
      borderColor: '',
      borderWidth: 0,
      hideFromLegend: false,

      type: 'line',
    },
    {
      label: isCoverage ? 'Required coverage' : 'Required Stock',
      dataKey: isCoverage ? 'reqCover' : 'requiredQuantity',
      borderColor: 'red',
      backgroundColor: 'transparent',
      borderWidth: 0,
      type: 'line',
      fill: false,
      hidden: !isCoverage, // Hide from chart but show in legend    },
      hideFromLegend: true,
    },
  ];

  const pluginsFactory = useMemo(() => () => [requiredStockLinePlugin], []);

  // Create a key that changes when the actual data changes, not just materialId
  const dataKey = useMemo(() => {
    const firstItem = logisticsData[0];
    const dataHash = firstItem ? `${firstItem.covPercentage}-${firstItem.currCover}-${firstItem.month}` : 'empty';
    return `logistics-chart-${materialId}-${isCoverage ? 'coverage' : 'logistics'}-${dataHash}`;
  }, [logisticsData, materialId, isCoverage]);

  return (
    <StrategyChart
      title={`${materialName} - ${isCoverage ? 'Coverage ratio' : 'Logistics inventory management'}`}
      materialId={materialId}
      data={logisticsData}
      matPrice={matPrice}
      unit={unit}
      onTimeChange={onTimeChange}
      datasets={datasets}
      pluginsFactory={pluginsFactory}
      stacked={true}
      showPercentageLabels={isCoverage}
      extraParams={{ logistics: true, isCoverage }}
      chartKey={dataKey}
    />
  );
};
